/**
 * Application analytics tracking
 * Wrapper around Segment Analytics with event queue management
 * Handles event tracking, user identification, and page views
 */

/** Analytics event data */
interface QueuedEvent {
  type: 'track' | 'identify' | 'page' | 'reset'
  item?: {
    event?: string
    properties?: Record<string, unknown>
  }
}

/**
 * Application Analytics class
 * Manages Segment analytics with queueing for delayed script loading
 */
class AppAnalytics {
  /** Event queue (for when analytics not yet loaded) */
  private queue: QueuedEvent[] = []

  /** Event name constants */
  public readonly events = {
    BUTTON_CLICKED: 'button_clicked',
    USER_LOGGED_IN_FROM_LOGIN_AS_ADMIN_URL: 'user_logged_in_from_login_as_admin_url',

    // Rate Connect
    RC_REVIEWS_READ_MORE_CLICKED: 'rc_reviews_read_more_clicked',
    RC_CREATE_CAMPAIGN_CLICKED: 'rc_create_campaign_clicked',
    RC_ONBOARDING_PAYMENT_SUCCESS: 'rc_onboarding_payment_success',
    RC_PAYMENT_VISITED: 'rc_payment_visited',
    RC_ONBOARDING_STARTED: 'rc_onboarding_started',
    RC_PROVIDER_CHANGE_SUCCESS: 'rc_provider_change_success',
    RC_PROVIDER_CHANGE_FAILED: 'rc_provider_change_failed',
    RC_RESUME_CAMPAIGN_CLICKED: 'rc_resume_campaign_clicked',
    RC_RESUME_CAMPAIGN_REDIRECT_TO_BILLING_CLICKED: 'rc_resume_campaign_redirect_to_billing_clicked',
    RC_RESUME_CAMPAIGN_CANCEL_CLICKED: 'rc_resume_campaign_cancel_clicked',
    RC_RESUME_CAMPAIGN_HOTELIER_SUPPORT_CLICKED: 'rc_resume_campaign_hotelier_support_clicked',
    RC_RESUME_CAMPAIGN_ADD_NEW_PAYMENT_METHOD_EXPIRED_NOT_VALID:
      'rc_resume_campaign_add_new_payment_method_expired_not_valid',
    RC_RESUME_CAMPAIGN_ADD_NEW_PAYMENT_METHOD_SOON_EXPIRED: 'rc_resume_campaign_add_new_payment_method_soon_expired',
    TRC_CAMPAIGN_RESUMED: 'tRC_campaign_resumed',
    RC_PAUSE_CAMPAIGN_BUTTON_CLICKED_SETTINGS: 'rc_pause_campaign_button_clicked_settings',
    RC_PAUSE_CAMPAIGN_REASON_SELECTED: 'rc_pause_campaign_reason_selected',
    RC_PAUSE_CAMPAIGN_CONTINUE_BUTTON_CLICKED: 'rc_pause_campaign_continue_button_clicked',
    RC_PAUSE_CAMPAIGN_2_KEEP_RATES_LIVE: 'rc_pause_campaign_2_keep_rates_live',
    RC_PAUSE_CAMPAIGN_CHANGE_CAMPAIGN_TYPE: 'rc_pause_campaign_change_campaign_type',
    RC_PAUSE_CAMPAIGN_IN_FLOW_BUTTON_CLICKED: 'rc_pause_campaign_in_flow_button_clicked',
    RC_PAUSE_CAMPAIGN_3_KEEP_RATES_LIVE: 'rc_pause_campaign_3_keep_rates_live',
    RC_PAUSE_CAMPAIGN_SEND_FEEDBACK: 'rc_pause_campaign_send_feedback',
    RC_PAUSE_CAMPAIGN_NO_FEEDBACK: 'rc_pause_campaign_no_feedback',
    RC_PAUSE_CAMPAIGN_TYPE_CHANGE_SUCCESS: 'rc_pause_campaign_type_change_success',
    RC_PAUSE_CAMPAIGN_CONTINUE_BUTTON_ON_REASON_SELECTION_CLICKED:
      'rc_pause_campaign_continue_button_on_reason_selection_clicked',
    RC_PAUSE_CAMPAIGN_CONTINUE_BUTTON_ON_PAUSE_TYPE_SELECTION_CLICKED:
      'rc_pause_campaign_continue_button_on_pause_type_selection_clicked',
    RC_PAUSE_CAMPAIGN_SET_DATE_UNTIL: 'rc_pause_campaign_set_date_until',
    RC_RED_PAUSE_CAMPAIGN_BUTTON_CLICKED: 'rc_red_pause_campaign_button_clicked',
    RC_PAUSE_UNTIL_FLOW_CANCELLED: 'rc_pause_until_flow_cancelled',
    RC_PAUSE_UNTIL_PAYMENT_INFO_ADD_PAYMENT_CLICKED: 'rc_pause_until_payment_info_add_payment_clicked',
    RC_OPPORTUNITY_PLUS_REDIRECTION: 'rc_opportunity_plus_redirection',
    RC_PLUS_CROSS_SELL_INLINE_CONTINUE_CLICKED: 'rc_plus_cross_sell_inline_continue_clicked',
    RC_PLUS_CROSS_SELL_POPUP_CONTINUE_CLICKED: 'rc_plus_cross_sell_popup_continue_clicked',
    RC_PLUS_CROSS_SELL_POPUP_REMIND_LATER_CLICKED: 'rc_plus_cross_sell_popup_remind_later_clicked',
    RC_PLUS_CROSS_SELL_POPUP_CLOSED: 'rc_plus_cross_sell_popup_closed',
    RC_OPPORTUNITY_SELECT_COMMISSION: 'rc_opportunity_select_commission',
    RC_OPPORTUNITY_CHANGE_COMMISSION: 'rc_opportunity_change_commission',
    RC_OPPORTUNITY_CONTACT_US_REDIRECTION: 'rc_opportunity_contact_us_redirection',
    RC_OPPORTUNITY_SETTINGS_REDIRECTION: 'rc_opportunity_settings_redirection',
    RC_LOGIN_AS_ADMIN_CLICKED: 'rc_login_as_admin_clicked',
    RC_STATUS_BAR_DOTS_CLICKED: 'rc_status_bar_dots_clicked',
    RC_STATUS_BAR_CARD_CLICKED: 'rc_status_bar_card_clicked',
    RC_STATUS_BAR_MENU_OPTION_CLICKED: 'rc_status_bar_menu_option_clicked',
    RC_SETTINGS_CHANGE_CAMPAIGN_CLICKED: 'rc_settings_change_campaign_clicked',
    RC_SETTINGS_CHANGE_PARTNER_CLICKED: 'rc_settings_change_partner_clicked',
    RC_SETTINGS_EDIT_BILLING_CLICKED: 'rc_settings_edit_billing_clicked',
    RC_NOT_ELIGIBLE_BOOKING_ENGINE_CONNECTED: 'rc_not_eligible_booking_engine_connected',
    RC_NOT_ELIGIBLE_NO_BOOKING_ENGINE: 'rc_not_eligible_no_booking_engine',
    RC_NOT_ELIGIBLE_NOT_LISTED_PARTNER: 'rc_not_eligible_not_listed_partner',
    RC_NOT_ELIGIBLE_CUSTOM_PARTNER: 'rc_not_eligible_custom_partner',
    RC_SMART_BOOST_DATES_SELECTED: 'rc_smart_boost_dates_selected',
    RC_SMART_BOOST_COMMISSION_SELECTED: 'rc_smart_boost_commission_selected',
    RC_SMART_BOOST_NEW_BOOST_CREATED: 'rc_smart_boost_new_boost_created',
    RC_SMART_BOOST_DELETE_ICON_CLICKED: 'rc_smart_boost_delete_icon_clicked',
    RC_SMART_BOOST_DELETED: 'rc_smart_boost_deleted',
    RC_SMART_BOOST_EDIT_COMMISSION: 'rc_smart_boost_edit_commission',
    RC_SMART_BOOST_UPDATED: 'rc_smart_boost_updated',
    RC_WELCOME_BANNER_PLAY_VIDEO_CLICKED: 'rc_welcome_banner_play_video_clicked',
    RC_STATUS_BAR_PLAY_VIDEO_CLICKED: 'rc_status_bar_play_video_clicked',
    RC_WELCOME_VIDEO_CLOSED: 'rc_welcome_video_closed',
    RC_TAKE_TOUR_CLICKED: 'rc_take_tour_clicked',
    RC_END_TOUR_CLICKED: 'rc_end_tour_clicked',
    RC_WELCOME_BANNER_CLOSED: 'rc_welcome_banner_closed',
    RC_GUIDE_DOWNLOADED: 'rc_guide_downloaded',
    RC_RATE_CHECK_STARTED: 'rc_rate_check_started',
    RC_RATE_CHECK_SUCCESS: 'rc_rate_check_success',
    RC_RATE_CHECK_FAILURE_CONTINUE: 'rc_rate_check_failure_continue',
    RC_RATE_CHECK_UNKNOWN_CONTINUE: 'rc_rate_check_unknown_continue',
    RC_RATE_CHECK_CONTACT_US_CLICKED: 'rc_rate_check_contact_us_clicked',

    // Studio Plus
    SP_HOMEPAGE_BANNER_CLICKED: 'sp_homepage_banner_clicked',
    SP_COMPARE_FEATURES_VISITED: 'sp_compare_features_visited',
    SP_CLICK_UPGRADE_BANNER: 'sp_click_upgrade_banner',
    SP_CLICK_UPGRADE_PAIN_POINTS: 'sp_click_upgrade_pain_points',
    SP_CLICK_RI_TILE: 'sp_click_ri_tile',
    SP_CLICK_VP_TILE: 'sp_click_vp_tile',
    SP_CLICK_HO_TILE: 'sp_click_ho_tile',
    SP_CLICK_HC_TILE: 'sp_click_hc_tile',
    SP_CLICK_RI_VIDEO: 'sp_click_ri_video',
    SP_CLICK_VP_VIDEO: 'sp_click_vp_video',
    SP_CLICK_HC_VIDEO: 'sp_click_hc_video',
    SP_CLICK_HO_VIDEO: 'sp_click_ho_video',
    SP_SHOW_OFFERS: 'sp_show_offers',
    SP_UPGRADE_FEATURE_COMPARISON: 'sp_upgrade_feature_comparison',
    SP_PAYMENT_VISITED: 'sp_payment_visited',
    SP_SELECT_CURRENCY_PAYMENT_PAGE: 'sp_select_currency_payment_page',
    SP_START_PLUS_SUBSCRIPTION_BUTTON_CLICKED: 'sp_start_plus_subscription_button_clicked',
    SP_PAYMENT_SUCCEED: 'sp_payment_succeed',
    SP_PAYMENT_FAILED: 'sp_payment_failed',
    SP_SUBSCRIPTION_CANCELED: 'sp_subscription_canceled',
    SP_SUBSCRIPTION_REACTIVATED: 'sp_subscription_reactivated',
    SP_BOOK_APPOINTMENT: 'sp_book_appointment',
    SP_KEEP_SUBSCRIPTION: 'sp_keep_subscription',
    SP_CANCEL_SUBSCRIPTION: 'sp_cancel_subscription',
    SP_REACTIVATION_CANCEL_BTN: 'sp_reactivation_cancel_btn',
    SP_ADD_VOUCHER_LINK: 'sp_add_voucher_link',
    SP_APPLY_VOUCHER: 'sp_apply_voucher',
    SP_REMOVE_VOUCHER: 'sp_remove_voucher',
    RC_ADD_VOUCHER_LINK: 'rc_add_voucher_link',
    RC_APPLY_VOUCHER: 'rc_apply_voucher',
    RC_REMOVE_VOUCHER: 'rc_remove_voucher',
    RC_VOUCHER_AUTO_APPLIED: 'rc_voucher_auto_applied',
    SP_LOGIN_AS_ADMIN_CLICKED: 'sp_login_as_admin_clicked',
    SP_START_SUBSCRIPTION_LANDING_CLICKED: 'sp_start_subscription_landing_clicked',
    SP_GET_STARTED_BTN_LANDING_CLICKED: 'sp_get_started_btn_landing_clicked',
    SP_HOTEL_OFFERS_BANNER_UPSELL_CLICK: 'sp_hotel_offers_banner_upsell_click',
    SP_PLUS_CHURN_INTRO_SHOWN: 'sp_plus_churn_intro_shown',
    SP_PLUS_CHURN_INTRO_CONTINUE_CLICKED: 'sp_plus_churn_intro_continue_clicked',
    SP_PLUS_CHURN_INTRO_KEEP_BENEFITS_CLICKED: 'sp_plus_churn_intro_keep_benefits_clicked',
    SP_PLUS_CHURN_INTRO_BOOK_CS_CLICKED: 'sp_plus_churn_intro_book_cs_clicked',
    SP_CONTINUE_CANCELLATION: 'sp_continue_cancellation',
    SP_SMART_TIPS_LINK_CLICKED: 'sp_smart_tips_link_clicked',
    SP_PLUS_INSIGHTS_STATUS_INDICATOR_CLICKED: 'sp_plus_insights_status_indicator_clicked',
    SP_PLUS_INSIGHTS_REACTIVATION_CTA_CLICKED: 'sp_plus_insights_reactivation_cta_clicked',
    SP_PLUS_INSIGHTS_PERFORMANCE_VIEWED: 'sp_plus_insights_performance_viewed',
    SP_PLUS_INSIGHTS_PERFORMANCE_EMPTY_STATE_VIEWED: 'sp_plus_insights_performance_empty_state_viewed',
    SP_PLUS_INSIGHTS_BENEFITS_VIEWED: 'sp_plus_insights_benefits_viewed',
    SP_PLUS_INSIGHTS_BENEFIT_CARD_CLICKED: 'sp_plus_insights_benefit_card_clicked',
    SP_PLUS_FEATURE_ACTIVATED_FROM_INSIGHTS: 'sp_plus_feature_activated_from_insights',
    SP_PLUS_INSIGHTS_SMART_TIPS_VIEWED: 'sp_plus_insights_smart_tips_viewed',
    SP_PLUS_INSIGHTS_TIP_FILTER_CLICKED: 'sp_plus_insights_tip_filter_clicked',
    SP_PLUS_INSIGHTS_TIP_IMAGES_CLICKED: 'sp_plus_insights_tip_images_clicked',
    SP_PLUS_INSIGHTS_RESOURCES_VIEWED: 'sp_plus_insights_resources_viewed',
    SP_PLUS_INSIGHTS_VIDEO_PLAYED: 'sp_plus_insights_video_played',
    SP_PLUS_INSIGHTS_VIDEO_COMPLETED: 'sp_plus_insights_video_completed',
    SP_PLUS_INSIGHTS_GUIDE_DOWNLOADED: 'sp_plus_insights_guide_downloaded',
    SP_PLUS_INSIGHTS_CONSULTATION_CTA_CLICKED: 'sp_plus_insights_consultation_cta_clicked',

    // New Home Page
    HOMEPAGE_LOCK_ICON_CLICKED: 'homepage_lock_icon_clicked',
    HOMEPAGE_LOCK_ICON_START_CAMPAIGN_CLICKED: 'homepage_lock_icon_start_campaign_clicked',
    HOMEPAGE_LOCK_ICON_LEARN_MORE_CLICKED: 'homepage_lock_icon_learn_more_clicked',
    HOMEPAGE_LOCK_ICON_POPUP_CANCELLED: 'homepage_lock_icon_popup_cancelled',
    HOMEPAGE_CARDS_SHOW_CHART: 'homepage_cards_show_chart',
    HOMEPAGE_SWITCH_GRAPH_BUTTON_CLICKED: 'homepage_switch_graph_button_clicked',
    HOMEPAGE_RATE_CONNECT_IMPRESSIONS_CLICKED: 'homepage_rate_connect_impressions_clicked',
    HOMEPAGE_RATE_CONNECT_BOOKINGS_CLICKED: 'homepage_rate_connect_bookings_clicked',
    HOMEPAGE_RATE_CONNECT_REVENUE_CLICKED: 'homepage_rate_connect_revenue_clicked',
    HOMEPAGE_RATE_CONNECT_CLICKS_CLICKED: 'homepage_rate_connect_clicks_clicked',
    HOMEPAGE_RATE_CONNECT_CPC_UPSELL_BTN_CLICKED: 'homepage_rate_connect_cpc_upsell_btn_clicked',
    SP_HOMEPAGE_PLUS_LEARN_MORE_BTN_CLICKED: 'sp_homepage_plus_learn_more_btn_clicked',
    SP_HOMEPAGE_PLUS_LOGIN_AS_ADMIN_BTN_CLICKED: 'sp_homepage_plus_login_as_admin_btn_clicked',
    SP_HOMEPAGE_PLUS_LEARN_MORE_VIDEO_PLAYED: 'sp_homepage_plus_learn_more_video_played',
    HOMEPAGE_RATE_CONNECT_LEARN_MORE_BTN_CLICKED: 'homepage_rate_connect_learn_more_btn_clicked',
    HOMEPAGE_RATE_CONNECT_LEARN_MORE_VIDEO_PLAYED: 'homepage_rate_connect_learn_more_video_played',
    HOMEPAGE_PERFORMANCE_NO_DATA_START_RC: 'homepage_performance_no_data_start_rc',
    HOMEPAGE_PERFORMANCE_NO_DATA_UNLOCK_HO: 'homepage_performance_no_data_unlock_ho',
    HOMEPAGE_PERFORMANCE_NO_DATA_UPDATE_PD: 'homepage_performance_no_data_update_pd',
    HOMEPAGE_RATE_CONNECT_LAUNCH_CAMPAIGN: 'homepage_rate_connect_launch_campaign',
    HOMEPAGE_PLUS_UPGRADE_NOW: 'homepage_plus_upgrade_now',
    HOMEPAGE_SIDEBAR_PLUS_LEARN_MORE_CLICKED: 'homepage_sidebar_plus_learn_more_clicked',
    HOMEPAGE_SIDEBAR_RATE_CONNECT_LEARN_MORE_CLICKED: 'homepage_sidebar_rate_connect_learn_more_clicked',
    HOMEPAGE_SIDEBAR_PROPERTY_DETAILS_CLICKED: 'homepage_sidebar_property_details_clicked',
    HOMEPAGE_SIDEBAR_HOTELIER_HUB: 'homepage_sidebar_hotelier_hub',
    HOMEPAGE_RC_MANAGE_CAMPAIGN: 'homepage_rc_manage_campaign',
    HOMEPAGE_RC_TIMEFRAME_CHANGED: 'homepage_rc_timeframe_changed',
    HOMEPAGE_PERFORMANCE_TIMEFRAME_CHANGED: 'homepage_performance_timeframe_changed',
    HOMEPAGE_RI_CLICKED: 'homepage_ri_clicked',
    HOMEPAGE_VP_CLICKED: 'homepage_vp_clicked',
    HOMEPAGE_SIDEBAR_PLUS_ACTIVE_HO: 'homepage_sidebar_plus_active_ho',
    HOMEPAGE_SIDEBAR_PLUS_ACTIVE_RI: 'homepage_sidebar_plus_active_ri',
    HOMEPAGE_SIDEBAR_PLUS_ACTIVE_VP: 'homepage_sidebar_plus_active_vp',
    HOMEPAGE_SIDEBAR_PLUS_ACTIVE_PD: 'homepage_sidebar_plus_active_pd',
    HOMEPAGE_PLUS_CREATE_NEW_OFFER: 'homepage_plus_create_new_offer',

    // Rate Insights
    RI_NO_BOOKING_SITES: 'ri_no_booking_sites',
    RI_NO_DATA_FOR_FILTERS: 'ri_no_data_for_filters',

    // Visitors Profile
    VP_TIMEFRAME_CHANGED: 'vp_timeframe_changed',

    // Studio Onboarding
    ONBOARDING_PERSONAL_INFORMATION_FORM: 'onboarding_personal_information_form',
    ONBOARDING_ASSIGN_ACCOMMODATION: 'onboarding_assign_accommodation',
    ONBOARDING_CANNOT_FIND_PROPERTY: 'onboarding_cannot_find_property',
    ONBOARDING_PROPERTY_ASSIGNED_LINK: 'onboarding_property_assigned_link',

    // Property details
    PD_ADD_POPULAR_FILTER_CLICKED: 'pd_add_popular_filter_clicked',
    PD_CHECK_PROPERTY_LINK_CLICKED: 'pd_check_property_link_clicked',
    PD_HOTEL_CONNECT_UPDATE_BTN: 'pd_hotel_connect_update_btn',
    PD_HOTEL_CONNECT_REACTIVATE_BTN: 'pd_hotel_connect_reactivate_btn',
    PD_CLOSE_MAIN_IMAGE_BEST_PRACTICES: 'pd_close_main_image_best_practices',
    PD_OPEN_MAIN_IMAGE_BEST_PRACTICES: 'pd_open_main_image_best_practices',
    PD_OPEN_IMAGE_QUALITY: 'pd_open_image_quality',
    PD_OPEN_IMAGES_NOT_PERMITTED: 'pd_open_images_not_permitted',
    PD_ADD_NEW_IMAGES_OPEN: 'pd_add_new_images_open',
    PD_NEW_IMAGES_UPLOADED: 'pd_new_images_uploaded',
    PD_OPEN_SET_MAIN_IMAGE: 'pd_open_set_main_image',
    PD_NEW_MAIN_IMAGE_SET: 'pd_new_main_image_set',

    // Hotel Offers
    HO_POST_OFFER_INITIAL_BANNER_CLICKED: 'ho_post_offer_initial_banner_clicked',
    HO_CATEGORY_FILTERS_CLICKED: 'ho_category_filters_clicked',
    HO_OFFER_CLICKED: 'ho_offer_clicked',
    HO_FEEDBACK_LINK_CLICKED: 'ho_feedback_link_clicked',
    HO_REMOVE_OFFER_CLICKED: 'ho_remove_offer_clicked',
    HO_UPDATE_OFFER_CLICKED: 'ho_update_offer_clicked',
    HO_SHOW_PROFILE_ON_TRIVAGO_CLICKED: 'ho_show_profile_on_trivago_clicked',

    // Landing page
    LP_SLIDE_ONE_REGISTER_NOW_CLICKED: 'lp_slide_one_register_now_clicked',
    LP_SLIDE_BUSINESS_STUDIO_REGISTER_NOW_CLICKED: 'lp_slide_business_studio_register_now_clicked',
    LP_SLIDE_STUDIO_PLUS_START_SUBSCRIPTION_CLICKED: 'lp_slide_studio_plus_start_subscription_clicked',
    LP_SLIDE_RATE_CONNECT_START_CAMPAIGN_CLICKED: 'lp_slide_rate_connect_start_campaign_clicked',
    LP_RATE_CONNECT_START_CAMPAIGN_CLICKED: 'lp_rate_connect_start_campaign_clicked',
    LP_RATE_CONNECT_GET_STARTED_BANNER_CLICKED: 'lp_rate_connect_get_started_banner_clicked',
    LP_LOGIN_REGISTER_BUTTON_CLICKED: 'lp_login_register_button_clicked',
    CHAT_LETS_TALK_CLICKED: 'chat_lets_talk_clicked',
    CHAT_CLOSE_CLICKED: 'chat_close_clicked',
    CHAT_PREDEFINED_OPTION_CLICKED: 'chat_predefined_option_clicked',
    CHAT_LINK_CLICKED: 'chat_link_clicked',

    // Billing
    BILLING_START_PLUS_SUBSCRIPTION: 'billing_start_plus_subscription',
    BILLING_REACTIVATE_PLUS_SUBSCRIPTION: 'billing_reactivate_plus_subscription',
    BILLING_RC_CHECK_CAMPAIGN: 'billing_rc_check_campaign',
    BILLING_REVIEW_INVOICES: 'billing_review_invoices',
    BILLING_RC_START_CAMPAIGN: 'billing_rc_start_campaign',
    BILLING_DOWNLOAD_INVOICE: 'billing_download_invoice',
    BILLING_CANCEL_PLUS_SUBSCRIPTION: 'billing_cancel_plus_subscription',

    // Pro to Plus
    PRO_TO_PLUS_MODAL_OPENED: 'pro_to_plus_modal_opened',
    PRO_TO_PLUS_REACTIVATION_CONFIRMED: 'pro_to_plus_reactivation_confirmed',
    PRO_TO_PLUS_MODAL_DISMISSED: 'pro_to_plus_modal_dismissed',
    PRO_TO_PLUS_SEE_HOW_IT_WORKS_CLICKED: 'pro_to_plus_see_how_it_works_clicked',
  } as const

  /** Retry timeout ID */
  private readTimeout: ReturnType<typeof setTimeout> | null = null

  /** Number of retry attempts */
  private readRetryCount = 0

  /**
   * Identify a user in Segment Analytics
   * @param id - User ID
   * @param properties - User properties/traits
   */
  identify(id = '', properties: Record<string, unknown> = {}): void {
    if (!id) return

    this.addToQueue('identify', {
      event: id,
      properties,
    })

    this.readQueue()
  }

  /**
   * Track an analytics event
   * @param event - Event name
   * @param properties - Event properties
   */
  track(event = '', properties: Record<string, unknown> = {}): void {
    if (!event) return

    let trafficSource: string
    try {
      trafficSource = window.sessionStorage?.getItem?.('traffic_source')?.trim() || ''
    } catch {
      trafficSource = ''
    }

    this.addToQueue('track', {
      event,
      properties: {
        ...properties,
        traffic_source: trafficSource,
      },
    })

    this.readQueue()
  }

  /**
   * Reset analytics (clear user identification)
   * Called on logout
   */
  reset(): void {
    if (window.analytics) {
      window.analytics.reset()
    } else {
      this.addToQueue('reset')
      this.readQueue()
    }
  }

  /**
   * Track page view
   * @param properties - Page properties
   */
  page(properties?: Record<string, unknown>): void {
    this.addToQueue('page', {
      properties,
    })

    this.readQueue()
  }

  /**
   * Add event to queue
   * @private
   */
  private addToQueue(type: QueuedEvent['type'], item?: QueuedEvent['item']): void {
    this.queue.push({
      type,
      item,
    })
  }

  /**
   * Process queued events when analytics is loaded
   * Retries until analytics is available or max retries reached
   * @private
   */
  private readQueue(): void {
    clearTimeout(this.readTimeout!)

    if (this.queue.length === 0 || this.readRetryCount >= 240) return

    if (!window.analytics) {
      // Retry after 500ms (max 240 retries = 2 minutes)
      this.readTimeout = setTimeout(() => {
        this.readRetryCount += 1
        this.readQueue()
      }, 500)

      return
    }

    let index = 0
    while (this.queue.length > 0) {
      const data = this.queue[index]
      const type = data.type
      const item = data.item

      switch (type) {
        case 'track':
          if (item?.event) {
            window.analytics.track?.(item.event, item.properties)
          }
          break

        case 'identify':
          if (item?.event) {
            window.analytics.identify?.(item.event, item.properties)
          }
          break

        case 'page':
          window.analytics.page?.(undefined, undefined, item?.properties)
          break

        case 'reset':
          window.analytics.reset?.()
          break
      }

      this.queue.splice(index, 1)
    }
  }
}

/** Singleton analytics instance */
export default new AppAnalytics()

/** Type of analytics events */
export type AnalyticsEvent = (typeof AppAnalytics.prototype.events)[keyof typeof AppAnalytics.prototype.events]
